import { MultiSelect } from "@bonliva-crm/ui";
import {
  ProfessionFilterModalInnerProps,
  encodeValue,
  toDecodedLabelValue,
  getProfessionToValue,
  renderSelectType,
  Option
} from "../../utils";
import { ComponentProps, useMemo } from "react";
import { useGlobalStore } from "@hooks";
import locale from "@bonliva-crm/locale";
import { SelectType } from "@bonliva-crm/shared";
import { ExperienceAndJobofferFilter } from "./common/experience-and-joboffer-filter";
import { isFilterSelected } from "../../../../utils/is-filter-selected";

export const RadioGrapherFilter = (props: ProfessionFilterModalInnerProps) => {
  const store = useGlobalStore();
  const professions = props?.value?.professions;

  const professionObject = useMemo(() => {
    return props?.value?.[getProfessionToValue(professions?.[0])];
  }, [props?.value, professions]);

  if (!("experience" in professionObject) || !("machines" in professionObject)) {
    return null;
  }

  const specs = store.cache
    .getAreaOfExpOnProfessions(professions)
    .flatMap((x) => x.Specializations)
    .unique()
    .sortBy((x) => x)
    .toLabelValue(
      (x) => x,
      (x) => encodeValue(professions?.[0], x)
    );

  const valueExp = professionObject?.experience?.value;
  const typeExp = professionObject?.experience?.type;
  const valueMachines = professionObject?.machines?.value;
  const typeMachines = professionObject?.machines?.type;

  const commonProps: ComponentProps<typeof MultiSelect<Option>> = {
    hideSelectedOptions: false,
    controlShouldRenderValue: true,
    hidePlaceholderOnFocus: false,
    optionType: "checkbox",
    showOptionDivider: false,
    isSearchable: true,
    maxMenuHeight: 400,
    closeMenuOnSelect: false,
    menuPlacement: "bottom" as const,
    styleProps: {
      menu: {
        position: "absolute",
        showBorder: true
      }
    }
  };

  const multiSelectSpecsProps: ComponentProps<typeof MultiSelect<Option>> = {
    ...commonProps,
    options: specs,
    value: toDecodedLabelValue(valueExp),
    placeholder: null,
    inputLabel: locale.Specialization,
    onChange: (selected) => {
      props.onChange({
        ...props?.value,
        [getProfessionToValue(professions?.[0])]: {
          experience: {
            value: selected.map((x) => x.value),
            type: typeExp
          },
          machines: {
            value: valueMachines,
            type: typeMachines
          }
        }
      });
    },
    showBadge: false,
    isClearable: isFilterSelected(props?.value?.radiographer.experience),
    onClear: () => {
      props.onChange({
        ...props?.value,
        radiographer: {
          ...props?.value?.radiographer,
          experience: {
            type: SelectType.ANY,
            value: []
          }
        },
        experienced: null,
        interested: null
      });
    },
    menuChildren: {
      prependedChildren: renderSelectType(typeExp, (t: SelectType) => {
        props.onChange({
          ...props?.value,
          [getProfessionToValue(professions?.[0])]: {
            experience: {
              value: valueExp,
              type: t
            },
            machines: {
              value: valueMachines,
              type: typeMachines
            }
          }
        });
      })
    }
  };

  const machines = store.cache.getMachinesOnProfession(professions?.[0]);

  const multiSelectMachinesProps: ComponentProps<typeof MultiSelect<Option>> = {
    ...commonProps,
    options: machines.toLabelValue(),
    value: valueMachines.toLabelValue(),
    placeholder: null,
    inputLabel: locale.Machines,
    onChange: (selected) => {
      props.onChange({
        ...props?.value,
        [getProfessionToValue(professions?.[0])]: {
          experience: {
            value: valueExp,
            type: typeExp
          },
          machines: {
            value: selected.map((x) => x.value),
            type: typeMachines
          }
        }
      });
    },
    showBadge: false,
    isClearable: isFilterSelected(props?.value?.radiographer.machines),
    onClear: () => {
      props.onChange({
        ...props?.value,
        radiographer: {
          ...props?.value?.radiographer,
          machines: {
            type: SelectType.ANY,
            value: []
          }
        }
      });
    },
    menuChildren: {
      prependedChildren: renderSelectType(typeMachines, (t: SelectType) => {
        props.onChange({
          ...props?.value,
          [getProfessionToValue(professions?.[0])]: {
            experience: {
              value: valueExp,
              type: typeExp
            },
            machines: {
              value: valueMachines,
              type: t
            }
          }
        });
      })
    }
  };

  return (
    <>
      <MultiSelect<Option> {...multiSelectSpecsProps} />
      <MultiSelect<Option> {...multiSelectMachinesProps} />
      {valueExp.length > 0 && <ExperienceAndJobofferFilter {...props} />}
    </>
  );
};
